/** Images on bugs and requests.
 *
 * Three pieces: a thumbnail that fetches its image with the user's token, a full-size
 * viewer, and a picker that takes a pasted screenshot as readily as a chosen file —
 * pasting is how most screenshots arrive, and a picker that only opens a file dialog
 * makes people save the image somewhere first.
 */

import { useEffect, useRef, useState } from 'react'
import { ImagePlus, X } from 'lucide-react'

import {
  ATTACHMENT_LIMITS,
  useAttachmentBlob,
  type FeedbackAttachment,
} from '@/features/feedback/useFeedback'

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** An object URL for a Blob or File, revoked when it changes or the component goes. */
function useObjectUrl(blob: Blob | undefined | null): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!blob) {
      setUrl(null)
      return
    }
    const next = URL.createObjectURL(blob)
    setUrl(next)
    return () => URL.revokeObjectURL(next)
  }, [blob])
  return url
}

/**
 * Full size, over everything. Escape is caught in the capture phase and stopped, so it
 * closes this and not whatever is underneath.
 */
function Lightbox({ url, name, onClose }: { url: string; name: string; onClose: () => void }) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={name}
    >
      <img src={url} alt={name} className="max-h-full max-w-full rounded shadow-2xl" />
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
        aria-label="Close"
      >
        <X size={18} aria-hidden />
      </button>
    </div>
  )
}

function Thumb({
  url,
  name,
  detail,
  onRemove,
  removing = false,
}: {
  url: string | null
  name: string
  detail: string
  onRemove?: () => void
  removing?: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <li className="group relative">
      <button
        type="button"
        onClick={() => url && setOpen(true)}
        className="block h-20 w-28 overflow-hidden rounded-md border border-border bg-canvas"
        title={`${name} · ${detail} — press to view full size`}
        disabled={!url}
      >
        {url ? (
          <img src={url} alt={name} className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full items-center justify-center text-[11px] text-fg-muted">
            Loading…
          </span>
        )}
      </button>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={removing}
          className="absolute -right-1.5 -top-1.5 rounded-full border border-border bg-surface p-0.5 text-fg-muted shadow hover:text-fg"
          aria-label={`Remove ${name}`}
          title="Remove this image"
        >
          <X size={12} aria-hidden />
        </button>
      ) : null}
      {open && url ? <Lightbox url={url} name={name} onClose={() => setOpen(false)} /> : null}
    </li>
  )
}

/** A stored attachment, fetched through the API. */
export function AttachmentThumb({
  itemId,
  attachment,
  onRemove,
  removing,
}: {
  itemId: string
  attachment: FeedbackAttachment
  onRemove?: () => void
  removing?: boolean
}) {
  const { data, isError } = useAttachmentBlob(itemId, attachment.id)
  const url = useObjectUrl(data)
  if (isError) {
    return (
      <li className="flex h-20 w-28 items-center justify-center rounded-md border border-border px-2 text-center text-[11px] text-fg-muted">
        Could not load {attachment.fileName}
      </li>
    )
  }
  return (
    <Thumb
      url={url}
      name={attachment.fileName}
      detail={formatSize(attachment.sizeBytes)}
      onRemove={onRemove}
      removing={removing}
    />
  )
}

/** A file chosen but not yet uploaded. */
export function PendingThumb({ file, onRemove }: { file: File; onRemove: () => void }) {
  const url = useObjectUrl(file)
  return (
    <Thumb url={url} name={file.name || 'Screenshot'} detail={formatSize(file.size)} onRemove={onRemove} />
  )
}

/**
 * The drop zone and "choose" button. Pasting is handled by the form around it (so a
 * paste anywhere in the form works), and it calls the same `onFiles`.
 */
export function ImagePicker({
  onFiles,
  disabled = false,
  held,
}: {
  onFiles: (files: File[]) => void
  disabled?: boolean
  held: number
}) {
  const input = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const full = held >= ATTACHMENT_LIMITS.maxPerItem

  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs text-fg-muted ${
        over ? 'border-[var(--brand)] bg-canvas' : 'border-border'
      }`}
      onDragOver={(event) => {
        event.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault()
        setOver(false)
        if (!disabled && !full) onFiles(Array.from(event.dataTransfer.files))
      }}
    >
      <ImagePlus size={14} aria-hidden />
      <span>
        {full
          ? `${ATTACHMENT_LIMITS.maxPerItem} images — the most an item can hold.`
          : 'Paste a screenshot (Ctrl+V), drop images here, or'}
      </span>
      {full ? null : (
        <button
          type="button"
          className="font-semibold text-fg underline disabled:opacity-50"
          onClick={() => input.current?.click()}
          disabled={disabled}
        >
          choose files
        </button>
      )}
      <span className="text-[11px]">PNG or JPEG, up to 5 MB each.</span>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg"
        multiple
        hidden
        onChange={(event) => {
          onFiles(Array.from(event.target.files ?? []))
          // Cleared so choosing the same file again still fires a change.
          event.target.value = ''
        }}
      />
    </div>
  )
}
